// Bouton tokenisé — mêmes variantes que le mobile (parité mentale).
// Polish premium : élévation douce au survol (rmbr-lift), halo coloré sur les CTA
// solides, enfoncement à l'appui, focus visible. Le curseur main est géré
// globalement (globals.css @layer base).

import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';
import { fontStyle } from '@/theme/typography';

type Variant = 'primary' | 'secondary' | 'ghost' | 'brand' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
}

// Variantes solides : ombre de repos + halo coloré au survol (effet « flottant »
// signature mobile). Ghost : pas d'élévation, juste un fond au survol.
const VARIANTS: Record<Variant, string> = {
  primary:
    'rmbr-lift bg-action text-action-on shadow-[var(--rmbr-shadow-card)] hover:bg-action-hover hover:shadow-[var(--rmbr-glow-action)]',
  brand:
    'rmbr-lift bg-brand text-brand-on shadow-[var(--rmbr-shadow-card)] hover:bg-brand-soft hover:shadow-[var(--rmbr-glow-brand)]',
  secondary:
    'rmbr-lift bg-action-muted text-action hover:bg-action-muted hover:shadow-[var(--rmbr-shadow-hover)]',
  danger:
    'rmbr-lift bg-danger text-action-on shadow-[var(--rmbr-shadow-card)] hover:brightness-[1.06]',
  ghost: 'bg-transparent text-action transition-colors hover:bg-action-muted active:scale-[0.98]',
};

const SIZES: Record<Size, string> = {
  sm: 'h-9 px-3.5 rounded-xl',
  md: 'h-11 px-5 rounded-xl',
  lg: 'h-12 px-6 rounded-xl',
};

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  disabled,
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      style={fontStyle('button')}
      className={cn(
        'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap',
        'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-action',
        'disabled:pointer-events-none disabled:opacity-55 disabled:shadow-none',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
      )}
    >
      {loading ? <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" /> : null}
      {children}
    </button>
  );
}
